import {
  formatDistance,
  formatYearsOff,
  QUESTIONS_PER_ROUND,
  REPORT_NOTE_MAX,
  REPORT_REASONS,
  SCORING,
  TIER_COLOR,
  TIER_EMOJI,
  TIER_LABEL,
  tierFor,
  type GuessReveal,
  type ReportReason,
  type Units,
} from '@geosport/shared';
import { useState } from 'react';
import { Button } from './Button';

type ReportState = 'closed' | 'open' | 'sending' | 'sent' | 'failed';

/** "Report a problem": a reason, an optional note, sent with the round's token. */
function ReportProblem({
  onReport,
}: {
  onReport: (reason: ReportReason, note: string) => Promise<boolean>;
}) {
  const [state, setState] = useState<ReportState>('closed');
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState('');

  if (state === 'closed') {
    return (
      <button
        type="button"
        className="self-start text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
        onClick={() => setState('open')}
        data-testid="report-open"
      >
        Report a problem with this question
      </button>
    );
  }
  if (state === 'sent') {
    return (
      <p role="status" className="text-xs text-emerald-300">
        Thanks. We check every report against the sources.
      </p>
    );
  }
  const send = async (): Promise<void> => {
    if (!reason) return;
    setState('sending');
    setState((await onReport(reason, note)) ? 'sent' : 'failed');
  };
  return (
    <div className="grid gap-2 rounded-xl bg-white/5 p-3 text-sm" data-testid="report-form">
      <div className="font-semibold">What’s wrong?</div>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Problem">
        {(Object.keys(REPORT_REASONS) as ReportReason[]).map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={reason === r}
            onClick={() => setReason(r)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              reason === r ? 'bg-accent text-ink' : 'bg-white/10 text-white/80'
            }`}
          >
            {REPORT_REASONS[r]}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={REPORT_NOTE_MAX}
        rows={2}
        placeholder="Optional: what should it be? A link helps."
        aria-label="Details (optional)"
        className="rounded-lg bg-black/30 p-2 text-sm placeholder:text-white/40"
      />
      {state === 'failed' && (
        <p className="text-xs text-red-300">That didn’t send. Please try again.</p>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => void send()}
          disabled={!reason || state === 'sending'}
          data-testid="report-send"
        >
          {state === 'sending' ? 'Sending…' : 'Send report'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setState('closed')}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function MiniBar({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-10 text-white/50">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
        <div
          className="h-full rounded-full transition-[width] duration-700"
          style={{ width: `${(value / max) * 100}%`, backgroundColor: color }}
        />
      </div>
      <span className="w-12 text-right font-semibold tabular-nums">
        {value}/{max}
      </span>
    </div>
  );
}

export function RevealCard({
  reveal,
  units,
  onNext,
  onReport,
}: {
  reveal: GuessReveal;
  units: Units;
  onNext: () => void;
  onReport?: (reason: ReportReason, note: string) => Promise<boolean>;
}) {
  const tier = tierFor(reveal.basePts);
  const color = TIER_COLOR[tier];
  const last = reveal.position === QUESTIONS_PER_ROUND;
  return (
    <div className="flex flex-col gap-3" data-testid="reveal-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-2xl font-black tabular-nums">
            <span role="img" aria-label={TIER_LABEL[tier]}>
              {TIER_EMOJI[tier]}
            </span>
            <span style={{ color }}>{reveal.basePts}</span>
            {reveal.multiplier > 1 && (
              <span className="text-base font-semibold text-white/60">(×{reveal.multiplier})</span>
            )}
            <span className="text-base font-semibold text-white/60">=</span>
            <span data-testid="question-points">+{reveal.questionPts} pts</span>
          </div>
          <div className="mt-0.5 text-sm text-white/60">
            {formatDistance(reveal.distanceKm, units)} off · {formatYearsOff(reveal.yearDiff)}
          </div>
        </div>
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold">
          {TIER_LABEL[tier]}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <MiniBar label="Where" value={reveal.wherePts} max={SCORING.wherePoints} color={color} />
        <MiniBar label="When" value={reveal.whenPts} max={SCORING.whenPoints} color={color} />
      </div>

      <div className="rounded-xl bg-white/5 p-3 text-sm leading-relaxed">
        <div className="mb-1 font-semibold">
          {reveal.answer.place} · {reveal.answer.year}
        </div>
        <p className="text-white/80">{reveal.story}</p>
      </div>

      {onReport && <ReportProblem key={reveal.position} onReport={onReport} />}

      <Button onClick={onNext} data-testid="next-button" className="w-full">
        {last ? 'See results →' : 'Next question →'}
      </Button>
    </div>
  );
}
